import React, { useState, useEffect } from 'react';
import {
  Typography,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import { CloudUpload, Close as CloseIcon } from '@mui/icons-material';
import {
  StyledModal,
  StyledTextField,
  StyledFormControl,
  ActionButton,
  UploadButton,
  ImagePreview,
  modalStyle
} from './StyledComponents';
import { 
  lookupService, 
  getImageUrl, 
  transformDataForServer, 
  getFormFields 
} from './lookupService';

const EditModal = ({ open, onClose, editingItem, lookupType, onSave, enqueueSnackbar }) => {
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [topics, setTopics] = useState([]); // For sub-topic topic dropdown

  // Load topics when modal opens for sub-topic
  useEffect(() => {
    if (open && lookupType === 'question-subtopic') {
      lookupService.fetchItems('question-topic')
        .then(data => {
          setTopics(data.filter(t => t.status === 1));
        })
        .catch(err => {
          console.error('Error loading topics:', err);
          setTopics([]);
        });
    }
  }, [open, lookupType]);

  useEffect(() => {
    if (editingItem && editingItem !== 'CREATE') {
      // console.log('Setting form data for editing:', editingItem);
      // console.log('Lookup type:', lookupType);

      // Create a copy of the editing item with proper field mapping
      const mappedData = { ...editingItem };

      // Ensure status is properly set
      if (mappedData.status !== undefined) {
        mappedData.status = Number(mappedData.status);
      }

      // Ensure topic_type_id is a number for sub-topics
      if (lookupType === 'question-subtopic' && mappedData.topic_type_id !== undefined) {
        mappedData.topic_type_id = Number(mappedData.topic_type_id);
      }

      // console.log('Mapped form data:', mappedData);
      setFormData(mappedData);

      if (editingItem.display_image) {
        setImagePreview(getImageUrl(editingItem.display_image));
      }
    } else {
      // console.log('Setting default form data for create');
      // Set default values for new items
      const defaultValues = {
        status: 1 // Default to active
      };

      // Add specific default values for difficulty fields
      if (lookupType === 'difficulty') {
        defaultValues.base_points = 1000;
        defaultValues.bonus_points = 100;
        defaultValues.quick_time_bonus_multiplier = 2.0;
        defaultValues.late_time_bonus_multiplier = 0.5;
        defaultValues.wrong_time_bonus_multiplier = -1.0;
        defaultValues.wrong_penalty_time_multiplier = 2.0;
      }

      // Add specific default values for games-datestamps
      if (lookupType === 'games-datestamps') {
        defaultValues.game_id = 1; // Default to Quiz game
      }

      // Add specific default values for sub-topics
      if (lookupType === 'question-subtopic') {
        defaultValues.topic_type_id = '';
      }

      setFormData(defaultValues);
      setImagePreview(null);
    }
    setImageFile(null);
  }, [editingItem, open, lookupType]);

  const handleClose = () => {
    setFormData({});
    setImageFile(null);
    setImagePreview(null);
    onClose();
  };

  const handleInputChange = (field, value) => {
    // console.log(`Updating field ${field} with value:`, value, `(type: ${typeof value})`);
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // console.log('Updated form data:', updated);
      return updated;
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // console.log('Image file selected:', file.name, file.size, file.type);
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        // console.log('Image preview set');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // console.log('Starting save process...');
      // console.log('Current form data:', formData);
      // console.log('Lookup type:', lookupType);

      let finalData = { ...formData };

      // Basic validation
      const requiredFields = getFormFields(lookupType).filter(field => field.required);
      // console.log('Required fields:', requiredFields);

      for (const field of requiredFields) {
        if (!finalData[field.key] && finalData[field.key] !== 0) {
          // console.log(`Validation failed for field: ${field.key}`, finalData[field.key]);
          enqueueSnackbar(`${field.label} is required`, { variant: 'error' });
          return;
        }
      }

      // Additional validation for difficulty fields
      if (lookupType === 'difficulty') {
        // Validate base_time > 0
        if (finalData.base_time && finalData.base_time <= 0) {
          enqueueSnackbar('Base Time must be greater than 0', { variant: 'error' });
          return;
        }

        // Validate base_points > 0
        if (finalData.base_points && finalData.base_points <= 0) {
          enqueueSnackbar('Base Points must be greater than 0', { variant: 'error' });
          return;
        }

        // Validate bonus_points >= 0 if provided
        if (finalData.bonus_points && finalData.bonus_points < 0) {
          enqueueSnackbar('Bonus Points must be 0 or greater', { variant: 'error' });
          return;
        }

        // Validate wrong_penalty_time_multiplier >= 0
        if (finalData.wrong_penalty_time_multiplier && finalData.wrong_penalty_time_multiplier < 0) {
          enqueueSnackbar('Wrong Penalty Time Multiplier must be 0 or greater', { variant: 'error' });
          return;
        }
      }

      // Handle image upload and preservation
      if (imageFile) {
        // console.log('Uploading new image...');
        setUploading(true);
        const imagePath = await lookupService.uploadImage(imageFile);
        finalData.display_image = imagePath;
        // console.log('New image uploaded:', imagePath);
        setUploading(false);
      } else if (editingItem && editingItem !== 'CREATE' && editingItem.display_image) {
        // Preserve existing image if no new image is uploaded
        finalData.display_image = editingItem.display_image;
        // console.log('Preserving existing image:', editingItem.display_image);
      }

      // Transform data for server
      const transformedData = transformDataForServer(finalData, lookupType);
      // console.log('Final transformed data for server:', transformedData);

      await onSave(transformedData);
      // console.log('Save completed successfully');
      handleClose();
    } catch (error) {
      console.error('Error saving:', error);
      enqueueSnackbar(`Error saving data: ${error.message || 'Please try again.'}`, { variant: 'error' });
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const renderFormField = (field) => {
    if (field.type === 'select' && field.key === 'status') {
      const currentValue = formData[field.key];
      // console.log(`Rendering status field with value: ${currentValue} (type: ${typeof currentValue})`);

      return (
        <StyledFormControl fullWidth key={field.key} sx={{ mb: 2 }}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={currentValue !== undefined ? Number(currentValue) : 1}
            label={field.label}
            onChange={(e) => {
              // console.log('Status changed to:', e.target.value);
              handleInputChange(field.key, Number(e.target.value));
            }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </StyledFormControl>
      );
    }

    // Special handling for games-datestamps game_id field
    if (field.type === 'select' && field.key === 'game_id') {
      const currentValue = formData[field.key];
      // console.log(`Rendering game_id field with value: ${currentValue}`);

      return (
        <StyledFormControl fullWidth key={field.key} sx={{ mb: 2 }}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={currentValue || ''}
            label={field.label}
            onChange={(e) => {
              // console.log('Game changed to:', e.target.value);
              handleInputChange(field.key, Number(e.target.value));
            }}
          >
            <MenuItem value={1}>Quiz</MenuItem>
            <MenuItem value={2}>Live Quiz</MenuItem>
          </Select>
        </StyledFormControl>
      );
    }

    // Special handling for sub-topic topic_type_id field
    if (field.type === 'select' && field.key === 'topic_type_id' && field.optionsType === 'question-topic') {
      const currentValue = formData[field.key];
      // console.log(`Rendering topic_type_id field with value: ${currentValue}`);

      return (
        <StyledFormControl fullWidth key={field.key} sx={{ mb: 2 }}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={currentValue !== undefined && currentValue !== null ? Number(currentValue) : ''}
            label={field.label}
            onChange={(e) => {
              // console.log('Topic changed to:', e.target.value);
              handleInputChange(field.key, Number(e.target.value));
            }}
          >
            {topics.length === 0 ? (
              <MenuItem disabled value="">
                <em>Loading topics...</em>
              </MenuItem>
            ) : (
              topics.map((topic) => (
                <MenuItem key={topic.topic_type_id} value={topic.topic_type_id}>
                  {topic.display_name}
                </MenuItem>
              ))
            )}
          </Select>
        </StyledFormControl>
      );
    }

    const currentValue = formData[field.key];
    // console.log(`Rendering field ${field.key} with value:`, currentValue);

    // Handle textarea fields
    if (field.type === 'textarea') {
      return (
        <StyledTextField
          key={field.key}
          fullWidth
          label={field.label}
          multiline
          rows={4}
          value={currentValue || ''}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          sx={{ mb: 2 }}
        />
      );
    }

    return (
      <StyledTextField
        key={field.key}
        fullWidth
        label={field.label}
        type={field.type || 'text'}
        value={currentValue || ''}
        onChange={(e) => {
          let value = e.target.value;
          // Convert to number for number fields
          if (field.type === 'number') {
            value = value === '' ? '' : Number(value);
          }
          handleInputChange(field.key, value);
        }}
        placeholder={field.placeholder}
        required={field.required}
        inputProps={{
          min: field.min,
          step: field.step
        }}
        sx={{ mb: 2 }}
      />
    );
  };

  return (
    <StyledModal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: "1px solid #333",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
            {editingItem === 'CREATE' ? 'Create' : 'Edit'} {
              lookupType === 'difficulty' ? 'Difficulty Level' :
                lookupType === 'question-type' ? 'Question Type' :
                  lookupType === 'answer-type' ? 'Answer Type' :
                    lookupType === 'question-mood' ? 'Question Mood' :
                      lookupType === 'question-topic' ? 'Question Topic' :
                        lookupType === 'question-subtopic' ? 'Question Sub-Topic' :
                          lookupType === 'question-bank' ? 'Question Bank' :
                            lookupType === 'games' ? 'Game' :
                              lookupType === 'games-datestamps' ? 'Game Datestamp' : 'Item'
            }
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: '#ccc' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
          {getFormFields(lookupType).map(renderFormField)}

          {/* Image upload for difficulty levels */}
          {lookupType === 'difficulty' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
                Display Image
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <UploadButton
                    variant="outlined"
                    component="span"
                    startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </UploadButton>
                </label>
                {imagePreview && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ImagePreview src={imagePreview} alt="Preview" />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                      }}
                      sx={{ color: '#f44336' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 3,
            borderTop: "1px solid #333",
            flexShrink: 0,
          }}
        >
          <ActionButton variant="outlined" onClick={handleClose}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploading}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </ActionButton>
        </Box>
      </Box>
    </StyledModal>
  );
};

export default EditModal;
